// API helpers para fondos (biblioteca + guardados del user).
//
// Tabla `fondos` (biblioteca pública):
//   id (number), nombre, link_img (URL pública), categoria, rubros (text[])
//
// Tabla `fondos_personalizados` (subidos por user):
//   id (number), id_company (uuid), nombre, nombre_archivo (text), created_at
//
// Storage bucket `fondos_personalizados`:
//   path: `<idCompany>/full/<fileName>`
//   Las URLs se firman (TTL 1h).

import { supabase } from '../../lib/supabase';

const SAVED_BUCKET = 'fondos_personalizados';
const SIGNED_TTL = 3600;

// ─── Biblioteca (global) ──────────────────────────────────────────────

export interface FondoBiblioteca {
  id: number;
  nombre: string;
  /** URL pública directa (los link_img de la biblioteca son públicos). */
  imageUrl: string;
  categoria: string | null;
  rubros: string[];
}

/** Trae fondos de biblioteca filtrando por rubro (si el user tiene rubro
 *  definido). Si `rubro` es null, devuelve todos. */
export async function fetchBibliotecaFondos(
  rubro: string | null,
): Promise<FondoBiblioteca[]> {
  let query = supabase
    .from('fondos')
    .select('id, nombre, link_img, categoria, rubros')
    .order('id', { ascending: true });
  if (rubro) {
    // Postgres `cs` operator: rubros array contains the user's rubro.
    query = query.contains('rubros', [rubro]);
  }
  const { data, error } = await query;
  if (error || !data) {
    console.error('[fondosApi] fetchBibliotecaFondos error', error);
    return [];
  }
  return (data as any[]).map((f): FondoBiblioteca => ({
    id: f.id as number,
    nombre: (f.nombre as string) || 'Fondo',
    imageUrl: (f.link_img as string) || '',
    categoria: (f.categoria as string) || null,
    rubros: Array.isArray(f.rubros) ? (f.rubros as string[]) : [],
  }));
}

// ─── Guardados del user ───────────────────────────────────────────────

export interface FondoGuardado {
  id: number;
  nombre: string;
  /** Nombre del archivo en el bucket (e.g. `1234-abcdef-mi-fondo.jpg`). */
  fileName: string;
  /** Signed URL ya resuelta — TTL 1h. */
  imageUrl: string;
}

function getSavedStoragePath(companyId: string, fileName: string): string {
  return `${companyId}/full/${fileName}`;
}

async function signFile(path: string): Promise<string> {
  const { data } = await supabase.storage
    .from(SAVED_BUCKET)
    .createSignedUrl(path, SIGNED_TTL);
  return data?.signedUrl || '';
}

/** Lista los fondos personalizados subidos por la empresa con URLs firmadas. */
export async function fetchFondosGuardados(
  companyId: string,
): Promise<FondoGuardado[]> {
  const { data, error } = await supabase
    .from('fondos_personalizados')
    .select('id, nombre, nombre_archivo')
    .eq('id_company', companyId)
    .order('created_at', { ascending: false });
  if (error || !data) {
    console.error('[fondosApi] fetchFondosGuardados error', error);
    return [];
  }
  const items = await Promise.all(
    (data as any[]).map(async (f): Promise<FondoGuardado> => {
      const fileName = (f.nombre_archivo as string) || '';
      const imageUrl = fileName
        ? await signFile(getSavedStoragePath(companyId, fileName))
        : '';
      return {
        id: f.id as number,
        nombre: (f.nombre as string) || 'Fondo',
        fileName,
        imageUrl,
      };
    }),
  );
  return items;
}

function buildFondoFileName(seed: string, ext: string): string {
  const slug = seed
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 40);
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${
    slug || 'fondo'
  }.${ext}`;
}

/** Sube un File al bucket + INSERT en `fondos_personalizados`. Devuelve
 *  el FondoGuardado recién creado con signed URL ya resuelta. */
export async function uploadFondo({
  companyId,
  file,
  name,
}: {
  companyId: string;
  file: File;
  name?: string;
}): Promise<FondoGuardado> {
  const trimmed = (name || '').trim();
  let effectiveName = trimmed;
  if (!effectiveName) {
    const { count } = await supabase
      .from('fondos_personalizados')
      .select('id', { count: 'exact', head: true })
      .eq('id_company', companyId);
    effectiveName = `Fondo ${(count ?? 0) + 1}`;
  }
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const fileName = buildFondoFileName(effectiveName, ext);
  const path = getSavedStoragePath(companyId, fileName);
  const { error: upErr } = await supabase.storage
    .from(SAVED_BUCKET)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || 'image/jpeg',
    });
  if (upErr) throw upErr;
  const { data: inserted, error: insErr } = await supabase
    .from('fondos_personalizados')
    .insert({
      id_company: companyId,
      nombre: effectiveName,
      nombre_archivo: fileName,
    })
    .select('id')
    .single();
  if (insErr) throw insErr;
  const imageUrl = await signFile(path);
  return {
    id: (inserted as any).id as number,
    nombre: effectiveName,
    fileName,
    imageUrl,
  };
}
